import React, {FC, memo, useCallback, useMemo} from 'react'
import {RootState} from '../../../setup'
import {Card2, ChartsWidget3, ListsWidget3} from '../../../_metronic/partials'
import {UseWindowSize} from '../../features/hooks/UseWindowSize'
import {useProducts, useDriver} from '../../@contexts'
import {Announcement} from './Announcement'
import {cardsDataInterface, DashboardWrapperTypes} from '../../@types'
import {shallowEqual, useSelector} from 'react-redux'
import {getCardsData} from './utils'
import {DriveStep} from 'driver.js'
import {GetDriverSteps, VerifyDateCondition} from '../../features'
import {ReviewsSlider} from './ReviewsSlider'

const styles = {
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gridTemplateRows: '1fr',
        gridGap: 10,
    },
    gridMd: {
        gridTemplateColumns: 'repeat(2, 1fr)',
    },
    gridSm: {
        gridTemplateColumns: 'repeat(2, 1fr)',
    },
}

type Props = {
    data?: DashboardWrapperTypes
}

const additionalSteps: DriveStep[] = [
    {
        element: `#dashboard-step-contacts`,
        popover: {
            title: `Étape contacts`,
            description: `Lorem ipsum dolor sit amet, consectetur adipisicing elit. Aperiam, earum saepe! Accusamus cum cumque eligendi ipsam iure, nemo quisquam saepe voluptas voluptatem! Cum deserunt doloremque eum necessitatibus neque odio sequi..`,
        },
    }, {
        element: `#dashboard-step-chart`,
        popover: {
            title: `Étape chart`,
            description: `Ceci est l'étape 12 pour chart.`,
        },
    }, {
        element: `#dashboard-step-announcement`,
        popover: {
            title: `Étape announcement`,
            description: `Ceci est l'étape 12 pour announcement.`,
        },
    }
]

const MemoizedCard2 = memo(Card2);

const Dashboard: FC<Props> = ({ data }) => {

    const { products: { callTracking, webForms, reviews }, loadingState} = useProducts();
    const {startDriver} = useDriver()
    const { pages: { dashboard } } = useSelector((state: RootState) => ({
        pages: state.driver.pages,
    }), shallowEqual);
    const { width } = UseWindowSize();

    const cardsData = useMemo(() => getCardsData(data, webForms, callTracking), [data, webForms, callTracking]);
    const enabledCards = useMemo(() => cardsData.filter((card) => card.enabled), [cardsData]);
    const isReviewsEnabled = useMemo(
        () => VerifyDateCondition(reviews.encrypted, reviews.activated_at),
        [reviews],
    );

    const getResponsiveStyles = useCallback(() => {
        const gridTemplateColumns = `repeat(${enabledCards.length}, 1fr)`;
        if (width <= 480) {
            return { ...styles.grid, ...styles.gridSm };
        } else if (width <= 768) {
            return { ...styles.grid, ...styles.gridMd };
        } else {
            return { ...styles.grid, gridTemplateColumns };
        }
    }, [width, enabledCards.length]);

    const stylesToApply = useMemo(() => getResponsiveStyles(), [getResponsiveStyles]);
    const steps = useMemo(() => GetDriverSteps<cardsDataInterface>({
        cards: enabledCards,
        additionalSteps,
        page: 'dashboard',
    }), [enabledCards]);

    const renderedCards = useMemo(
        () =>
            enabledCards.map(({ iconPath, body, to }, index) => (
                <div key={index} id={`dashboard-step-${index}`}>
                    <MemoizedCard2
                        bg={index % 2 === 0 ? 0 : 1}
                        iconPath={iconPath}
                        body={{ total: body.total, text: body.text }}
                        to={to}
                    />
                </div>
            )),
        [enabledCards],
    );

    // useStartDriver({
    //     loading: loadingState,
    //     pageFeatureFlag: dashboard,
    //     steps,
    //     pageName: 'dashboard',
    //     startDriver,
    // })

    return (
        <>
            <div className="mb-5" style={stylesToApply}>{renderedCards}</div>
            <div className="row d-flex justify-content-between mb-5">
                <div className="col-xl-4 card card-xl-stretch mb-5 mb-lg-0" id="dashboard-step-contacts">
                    <ListsWidget3 contacts={data?.dashboardLastContacts} />
                </div>
                <div className="col-xl-8 px-0 px-md-5" id="dashboard-step-chart">
                    <ChartsWidget3 className="card card-xl-stretch"
                                   chartOptions={data?.dashboardNumberOfVisits.chartOptions} />
                </div>
            </div>

            {isReviewsEnabled && (
                <div className="mb-10">
                    <ReviewsSlider data={data?.reviews_google} />
                </div>
            )}

            {data?.dashboardAnnouncement?.annonce_homepage_1 && (
                <div className="col-12" id="dashboard-step-announcement">
                    <Announcement announcement={data.dashboardAnnouncement.annonce_homepage_1} />
                </div>
            )}
        </>
    )
}

export {Dashboard}
